import {
  SEU_BORDER_STREAM_OFFSET,
  SEU_CANVAS_GRID_CELL_SIZE,
  SEU_MIN_LENGTH_FOR_ARROW,
  SEU_STREAM_HANDLE_SIZE_M,
  SEU_STREAM_HANDLE_SIZE_S,
  SeuCanvasStreamPositionItem,
  SeuStreamHandleSize,
} from '@seu-types';
import { hasStreamNaturalFlow } from './streams.helper';

type SeuStreamPathArrowDir = 'up' | 'down' | 'left' | 'right';

type SeuStreamPathArrow = {
  dir: SeuStreamPathArrowDir;
  x: number;
  y: number;
};
export type SeuStreamPathStep = {
  dx: number;
  dy: number;
};

export function getOffsetForStreamHandle(
  numberOfStreamHandles: number,
  streamHandleIndex: number,
  handleSize: SeuStreamHandleSize = 'M'
): number {
  // Handles are rendered centered (flex-col justify-center) inside the component card,
  // which itself is centered (flex items-center justify-center) in the 288×288 grid cell.
  // The offset from the grid cell bottom edge for handle i of N equals:
  //   cellCenter + distanceFromCenter
  // where distanceFromCenter = ((N-1)/2 - i) * handleSize
  // For N=1, i=0 this reduces to cellCenter (144px), matching the old single-handle case.

  const handleSizePx =
    handleSize === 'M' ? SEU_STREAM_HANDLE_SIZE_M : SEU_STREAM_HANDLE_SIZE_S;
  const distanceFromCenter =
    ((numberOfStreamHandles - 1) / 2 - streamHandleIndex) * handleSizePx;

  return SEU_CANVAS_GRID_CELL_SIZE / 2 + distanceFromCenter;
}

export function convertStreamToPathStepsAndArrows(
  stream: SeuCanvasStreamPositionItem,
  startXPixel: number,
  startYPixel: number,
  numberOfStreamHandlesStart: number,
  numberOfStreamHandlesEnd: number,
  outputHandleIndex: number,
  inputHandleIndex: number,
  outputSide: 'left' | 'right',
  inputSide: 'left' | 'right',
  streamHandleOffsetStartX: number,
  streamHandleOffsetEndX: number,
  outputHandleSize: SeuStreamHandleSize = 'M',
  inputHandleSize: SeuStreamHandleSize = 'M',
  isStreamBlocked: boolean
): { steps: SeuStreamPathStep[]; arrows: SeuStreamPathArrow[] } {
  const steps: SeuStreamPathStep[] = [];
  const arrows: SeuStreamPathArrow[] = [];

  const deltaY = stream.endY - stream.startY;
  const deltaYPixel = deltaY * SEU_CANVAS_GRID_CELL_SIZE;

  const deltaX = stream.endX - stream.startX;
  const deltaXPixel = deltaX * SEU_CANVAS_GRID_CELL_SIZE;

  const cummulatedPath = {
    x: startXPixel,
    y: startYPixel,
  };

  const offsetForStreamHandleStart = getOffsetForStreamHandle(
    numberOfStreamHandlesStart,
    outputHandleIndex,
    outputHandleSize
  );

  const offsetForStreamHandleEnd = getOffsetForStreamHandle(
    numberOfStreamHandlesEnd,
    inputHandleIndex,
    inputHandleSize
  );

  // The actual Y offset that was applied to startYPixel by the caller:
  const startYOffset = offsetForStreamHandleStart;

  // The exact SVG Y coordinate the path must reach (centre of the target input handle).
  const endYPixel =
    startYPixel - deltaYPixel + startYOffset - offsetForStreamHandleEnd;

  // NOTES
  // deltaYPixel is negative when the stream goes up
  // deltaXPixel is negative when the stream goes left

  // outputSide: which side of the output component the handle is on ('right' or 'left')
  // inputSide:  which side of the input component the handle is on ('right' or 'left')

  // The path always originates from startXPixel (already positioned at the handle face)
  // and must reach the input handle face on inputSide.

  // outDir = +1 if output exits rightward, -1 if exits leftward
  // inDir  = +1 if stream enters input to the right (path arrives from the left), -1 if enters from left (path arrives from right)

  const outDir = outputSide === 'right' ? 1 : -1;
  const inDir = inputSide === 'left' ? 1 : -1;

  // Whether output and input handle are both on the same side on their respective components
  const sameSide = outputSide === inputSide;

  // The effective horizontal distance between the two handles in grid cells,
  // measured in the outDir direction (positive = input is ahead of output).
  const effectiveDeltaX = outDir * deltaX;

  // Define three-segment streams, because they need extra handling in some cases
  const isThreeSegment =
    !sameSide &&
    effectiveDeltaX === 1 &&
    (deltaY !== 0 ||
      (deltaY === 0 &&
        offsetForStreamHandleStart !== offsetForStreamHandleEnd));

  const isSameSideThreeSegment = sameSide && effectiveDeltaX === 0;

  // Total X from startXPixel to endHandleFace:
  // The net X distance the middle horizontal segment must cover equals:
  // (end cell X) − (start cell X) in pixel space, adjusted for handle offsets, generalised using directions.
  const totalX =
    deltaXPixel +
    (sameSide ? 0 : -outDir * SEU_CANVAS_GRID_CELL_SIZE) +
    outDir * streamHandleOffsetStartX +
    inDir * streamHandleOffsetEndX;

  // If Stream goes straight (same row, adjacent in the natural flow direction, same handle offsets)
  // and is not disrupted, we can draw it as a single segment, directly from start to end.

  // A stream is considered flowing naturally if it goes from left to right with the output handle on the right and input handle on the left, or vice versa.
  const hasNaturalFlow = hasStreamNaturalFlow(
    stream.startX,
    stream.endX,
    inputSide,
    outputSide
  );

  if (
    hasNaturalFlow &&
    offsetForStreamHandleStart === offsetForStreamHandleEnd &&
    deltaY === 0 &&
    !isStreamBlocked
  ) {
    const pixelBetween = (Math.abs(deltaX) - 1) * SEU_CANVAS_GRID_CELL_SIZE;
    steps.push({
      dx:
        outDir *
        (streamHandleOffsetStartX + pixelBetween + streamHandleOffsetEndX),
      dy: 0,
    });

    const numberOfArrows = pixelBetween
      ? Math.floor(
          pixelBetween /
            (SEU_CANVAS_GRID_CELL_SIZE - 2 * SEU_BORDER_STREAM_OFFSET)
        )
      : 0;

    for (let i = 0; i < numberOfArrows; i++) {
      arrows.push({
        dir: outDir > 0 ? 'right' : 'left',
        x:
          startXPixel +
          outDir *
            (streamHandleOffsetStartX +
              (i + 1) * SEU_CANVAS_GRID_CELL_SIZE -
              SEU_CANVAS_GRID_CELL_SIZE / 2),
        y: startYPixel,
      });
    }

    return { steps, arrows };
  }

  // ----------------------
  // BASE SEGMENT VARIABLES
  // ----------------------

  // The initial horizontal offset from the start point to exit the output handle respecting the output direction
  let dxStart = outDir * streamHandleOffsetStartX;

  // we need to travel -deltaYPixel to reach the row of the end component.
  // negative, because in canvas Y grows downwards, while a positive deltaY means the stream goes upwards.
  let dyMain = -deltaYPixel;

  if (isThreeSegment || isSameSideThreeSegment) {
    // Move directly to the target handle Y in one step — no routing detour needed.
    dyMain = endYPixel - startYPixel;
  }

  let dxEnd = inDir * streamHandleOffsetEndX;

  let dxMain = totalX - dxStart - dxEnd;

  // ----------------
  // STARTING SEGMENT - exit the output handle horizontally
  // ----------------

  if (dyMain < 0) {
    dxStart += SEU_BORDER_STREAM_OFFSET;
  } else if (dyMain >= 0) {
    dxStart -= SEU_BORDER_STREAM_OFFSET;
  }

  steps.push({
    dx: dxStart,
    dy: 0,
  });

  cummulatedPath.x += dxStart;

  // ---------------------
  // MAIN VERTICAL SEGMENT
  // ---------------------

  if (!isThreeSegment && !isSameSideThreeSegment) {
    if (dxMain > 0) {
      dyMain += SEU_BORDER_STREAM_OFFSET;
    } else if (dxMain < 0) {
      dyMain -= SEU_BORDER_STREAM_OFFSET;
    }

    if (!isSameSideThreeSegment && deltaY < 0) {
      dyMain -= SEU_CANVAS_GRID_CELL_SIZE;
    }

    dyMain += offsetForStreamHandleStart;
  }

  if (dyMain !== 0) {
    steps.push({
      dx: 0,
      dy: dyMain,
    });

    // Add one arrow for every 288 pixels of dyMain, but only if the segment is long enough to fit an arrow (considering border offsets)
    const dyMainAbs = Math.abs(dyMain);
    let numberOfArrowsDy =
      Math.floor(
        dyMainAbs / (SEU_CANVAS_GRID_CELL_SIZE - 2 * SEU_BORDER_STREAM_OFFSET)
      ) || 1;

    if (dyMainAbs < SEU_MIN_LENGTH_FOR_ARROW) {
      numberOfArrowsDy = 0;
    }

    for (let i = 0; i < numberOfArrowsDy; i++) {
      const fullGridCellsUntilThisCell = Math.floor(
        cummulatedPath.y / SEU_CANVAS_GRID_CELL_SIZE
      );

      let firstArrowPosition =
        fullGridCellsUntilThisCell * SEU_CANVAS_GRID_CELL_SIZE;

      if (deltaY <= 0) {
        firstArrowPosition += 0.9 * SEU_CANVAS_GRID_CELL_SIZE;
      } else {
        firstArrowPosition += 0.1 * SEU_CANVAS_GRID_CELL_SIZE;
      }

      arrows.push({
        dir: dyMain <= 0 ? 'up' : 'down',
        x: cummulatedPath.x,
        y:
          firstArrowPosition +
          i * (dyMain <= 0 ? -1 : 1) * SEU_CANVAS_GRID_CELL_SIZE,
      });
    }
  }

  cummulatedPath.y += dyMain;

  // -----------------------
  // MAIN HORIZONTAL SEGMENT
  // -----------------------

  if (deltaY === 0) {
    dxMain += 2 * SEU_BORDER_STREAM_OFFSET;
  }

  if (isThreeSegment || isSameSideThreeSegment) {
    dxMain = 0;
  }

  if (dxMain !== 0) {
    steps.push({
      dx: dxMain,
      dy: 0,
    });

    // Add one arrow for every 288 pixels of dxMain
    const numberOfArrowsDx = Math.floor(
      Math.abs(dxMain) /
        (SEU_CANVAS_GRID_CELL_SIZE - SEU_BORDER_STREAM_OFFSET * 2)
    );
    for (let i = 0; i < numberOfArrowsDx; i++) {
      const arrowDistance =
        (i + 1) * SEU_CANVAS_GRID_CELL_SIZE - SEU_CANVAS_GRID_CELL_SIZE / 2;
      arrows.push({
        dir: dxMain < 0 ? 'left' : 'right',
        x: cummulatedPath.x + (dxMain < 0 ? -1 : 1) * arrowDistance,
        y: cummulatedPath.y,
      });
    }
  }

  cummulatedPath.x += dxMain;

  // ----------------------
  // FINAL VERTICAL SEGMENT
  // ----------------------

  let dyFinal = endYPixel - cummulatedPath.y;

  if (isThreeSegment || isSameSideThreeSegment) {
    // dyMain already moved us exactly to endYPixel; no final vertical correction needed.
    dyFinal = 0;
  }

  if (dyFinal !== 0) {
    steps.push({
      dx: 0,
      dy: dyFinal,
    });

    arrows.push({
      dir: dyFinal < 0 ? 'up' : 'down',
      x: cummulatedPath.x,
      y:
        cummulatedPath.y +
        (dyFinal < 0 ? -1 : 1) *
          (SEU_CANVAS_GRID_CELL_SIZE * 0.1 + SEU_BORDER_STREAM_OFFSET),
    });
  }

  // ---------------------
  // ENDING SEGMENT
  // ---------------------

  if (dyFinal > 0) {
    dxEnd += SEU_BORDER_STREAM_OFFSET;
  } else if (dyFinal < 0) {
    dxEnd -= SEU_BORDER_STREAM_OFFSET;
  }

  if (isThreeSegment) {
    if (dyMain > 0) {
      dxEnd += SEU_BORDER_STREAM_OFFSET;
    } else if (dyMain < 0) {
      dxEnd -= SEU_BORDER_STREAM_OFFSET;
    }
  }

  steps.push({
    dx: dxEnd,
    dy: 0,
  });

  return { steps, arrows };
}

export function buildSvgPath(
  startX: number,
  startY: number,
  steps: SeuStreamPathStep[]
): string {
  let d = `M ${startX} ${startY}`;

  // Amount already consumed from the start of the current step by the previous arc.
  const cornerRadius = 24;
  let skipAmount = 0;

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    const next = steps[i + 1];

    // Reduce the portion already covered by the incoming arc.
    let dx = step.dx;
    let dy = step.dy;
    if (skipAmount > 0) {
      if (dx !== 0) {
        dx -= Math.sign(dx) * skipAmount;
      } else {
        dy -= Math.sign(dy) * skipAmount;
      }
      skipAmount = 0;
    }

    // A direction change exists when one step is purely horizontal and the
    // next is purely vertical, or vice versa.
    const directionChanges =
      next !== undefined &&
      ((dx !== 0 && dy === 0 && next.dy !== 0 && next.dx === 0) ||
        (dy !== 0 && dx === 0 && next.dx !== 0 && next.dy === 0));

    if (directionChanges && next !== undefined) {
      const currentLen = Math.abs(dx !== 0 ? dx : dy);
      const nextLen = Math.abs(next.dx !== 0 ? next.dx : next.dy);
      // Clamp radius so it never exceeds half the length of either segment.
      const r = Math.min(cornerRadius, currentLen / 2, nextLen / 2);

      if (r > 0) {
        // Emit the shortened current segment (stop r pixels before the corner).
        const shortenedDx = dx !== 0 ? dx - Math.sign(dx) * r : 0;
        const shortenedDy = dy !== 0 ? dy - Math.sign(dy) * r : 0;
        if (shortenedDx !== 0 || shortenedDy !== 0) {
          d += ` l ${shortenedDx} ${shortenedDy}`;
        }

        // Sweep flag: cross product of incoming×outgoing in SVG coords (y-down).
        // Positive cross product → clockwise turn on screen → sweep=1.
        const cross = dx * next.dy - dy * next.dx;
        const sweep = cross > 0 ? 1 : 0;

        // Arc displacement = r*(unit_in + unit_out), which lands r past the corner
        // in the outgoing direction.
        const arcDx = Math.sign(dx) * r + Math.sign(next.dx) * r;
        const arcDy = Math.sign(dy) * r + Math.sign(next.dy) * r;
        d += ` a ${r} ${r} 0 0 ${sweep} ${arcDx} ${arcDy}`;

        // The next step already starts r into its direction.
        skipAmount = r;
        continue;
      }
    }

    if (dx !== 0 || dy !== 0) {
      d += ` l ${dx} ${dy}`;
    }
  }

  return d;
}

export function buildSvgArrow(arrow: SeuStreamPathArrow): string {
  switch (arrow.dir) {
    case 'up':
      return `M ${arrow.x} ${arrow.y} l -6 10 l 12 0 Z`;
    case 'down':
      return `M ${arrow.x} ${arrow.y} l -6 -10 l 12 0 Z`;
    case 'left':
      return `M ${arrow.x} ${arrow.y} l 10 -6 l 0 12 Z`;
    case 'right':
      return `M ${arrow.x} ${arrow.y} l -10 -6 l 0 12 Z`;
  }
}
